import { useEffect, useMemo, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import api from '../utils/api';
import { FEATURE_KEYS, DEFAULT_FEATURE_FLAGS, PLAN_LABELS, ensureRole } from '../utils/rbac';

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'estimator', label: 'Estimator' },
  { value: 'designer', label: 'Designer' },
  { value: 'viewer', label: 'Viewer' },
];

const card =
  'glass-card';
const input =
  'glass-input w-full text-sm';
const btnPrimary =
  'glass-cta text-sm disabled:opacity-60 disabled:cursor-not-allowed';

const emptyLaborDraft = () => ({
  name: '',
  jobTitle: '',
  crewNumber: '',
  hourlyRate: '',
  contactEmail: '',
  notes: '',
});

const FEATURE_CONTROLS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    blurb: 'Workspace overview cards and quick actions.',
  },
  {
    key: 'projects',
    label: 'Projects',
    blurb: 'Browse, open, and manage projects.',
  },
  {
    key: 'estimates',
    label: 'Estimates',
    blurb: 'Upload files and work with estimate outputs.',
  },
  {
    key: 'design',
    label: 'Design',
    blurb: 'Access the Design Assistant canvas.',
  },
  {
    key: 'admin',
    label: 'Admin Console',
    blurb: 'Manage teammates and workspace settings.',
  },
];

const FEATURE_FLAG_ALIAS_MAP = {
  dashboard: ['dashboard'],
  projects: ['projects'],
  estimates: ['estimates', 'uploads'],
  design: ['design', 'xr'],
  labor: ['labor'],
  admin: ['admin'],
};

const getFlagValue = (source, key) => {
  if (!source) return undefined;
  const keys = FEATURE_FLAG_ALIAS_MAP[key] || [key];
  for (const candidate of keys) {
    if (Object.prototype.hasOwnProperty.call(source, candidate)) {
      return source[candidate];
    }
  }
  return undefined;
};

const parseNumberField = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export default function AdminConsole() {
  const { organization, permissions, profile, refresh } = useOrg();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);
  const [laborProfiles, setLaborProfiles] = useState([]);
  const [laborLoading, setLaborLoading] = useState(true);
  const [laborDraft, setLaborDraft] = useState(() => emptyLaborDraft());
  const [laborCreateBusy, setLaborCreateBusy] = useState(false);
  const [laborSavingId, setLaborSavingId] = useState(null);
  const [laborDeletingId, setLaborDeletingId] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const loadMembers = async () => {
    setError('');
    setLoadingMembers(true);
    try {
      const { data } = await api.get('/org/members');
      const memberList = (data?.members || []).map((member) => ({
        ...member,
        feature_overrides: member.feature_overrides || {},
      }));
      setMembers(memberList);
      const pendingInvites = (data?.invites || []).filter((invite) => invite.status === 'pending');
      setInvites(pendingInvites);
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to load members.');
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadLabor = async () => {
    setLaborLoading(true);
    try {
      const { data } = await api.get('/org/labor');
      setLaborProfiles(data?.labor || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to load labor settings.');
    } finally {
      setLaborLoading(false);
    }
  };

  useEffect(() => {
    if (!permissions.canAccessAdmin && permissions.role !== 'admin') return;
    loadMembers();
    if (permissions.canAccessLabor) {
      loadLabor();
    } else {
      setLaborProfiles([]);
      setLaborLoading(false);
    }
  }, [permissions.canAccessAdmin, permissions.canAccessLabor, permissions.role]);

  useEffect(() => {
    if (!permissions.canAccessAdmin && permissions.role !== 'admin') return;
    if (!members.length) {
      setSelectedMemberId(null);
      return;
    }
    if (selectedMemberId && members.some((member) => member.id === selectedMemberId)) {
      return;
    }
    const preferred = members.find((member) => member.id === profile?.id) || members[0];
    if (preferred) {
      setSelectedMemberId(preferred.id);
    }
  }, [members, selectedMemberId, profile?.id, permissions.canAccessAdmin, permissions.role]);

  const handleInvite = async (event) => {
    event?.preventDefault();
    setError('');
    setSuccess('');
    setInviteBusy(true);
    try {
      if (!inviteEmail.trim()) throw new Error('Enter an email address.');
      await api.post('/org/invite', { email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail('');
      setInviteRole('viewer');
      setSuccess('Invitation sent.');
      loadMembers();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to send invite.');
    } finally {
      setInviteBusy(false);
    }
  };

  const updateRole = async (userId, role) => {
    setError('');
    setSuccess('');
    try {
      await api.patch(`/org/members/${userId}`, { role });
      setMembers((prev) => prev.map((member) => (member.id === userId ? { ...member, role } : member)));
      setSuccess('Role updated.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to update role.');
    }
  };

  const removeMember = async (userId) => {
    setError('');
    setSuccess('');
    try {
      await api.delete(`/org/members/${userId}`);
      setMembers((prev) => prev.filter((member) => member.id !== userId));
      setSuccess('Member removed.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to remove member.');
    }
  };

  const revokeInvite = async (inviteId) => {
    setError('');
    setSuccess('');
    try {
      await api.delete(`/org/invite/${inviteId}`);
      setInvites((prev) => prev.filter((invite) => invite.id !== inviteId));
      setSuccess('Invite revoked.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to revoke invite.');
    }
  };

  const draftToPayload = (draft) => ({
    name: draft.name?.trim(),
    job_title: draft.jobTitle?.trim() || null,
    crew_number: parseNumberField(draft.crewNumber),
    hourly_rate: parseNumberField(draft.hourlyRate),
    contact_email: draft.contactEmail?.trim() || null,
    notes: draft.notes?.trim() || null,
  });

  const profileToPayload = (profile) => ({
    name: profile.name?.trim(),
    job_title: profile.job_title?.trim() || null,
    crew_number: parseNumberField(profile.crew_number),
    hourly_rate: parseNumberField(profile.hourly_rate),
    contact_email: profile.contact_email?.trim() || null,
    notes: profile.notes?.trim() || null,
  });

  const resetLaborDraft = () => setLaborDraft(emptyLaborDraft());

  const handleLaborDraftChange = (field, value) => {
    setLaborDraft((prev) => ({ ...prev, [field]: value }));
  };

  const createLaborProfile = async (event) => {
    event?.preventDefault();
    setError('');
    setSuccess('');
    setLaborCreateBusy(true);
    try {
      const payload = draftToPayload(laborDraft);
      if (!payload.name) {
        throw new Error('Name is required.');
      }
      const { data } = await api.post('/org/labor', payload);
      const created = data?.labor;
      setLaborProfiles((prev) =>
        [...prev, created].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
      );
      resetLaborDraft();
      setSuccess('Labor profile added.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to add labor profile.');
    } finally {
      setLaborCreateBusy(false);
    }
  };

  const handleLaborFieldChange = (id, field, value) => {
    setLaborProfiles((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    );
  };

  const saveLaborProfile = async (entry) => {
    setError('');
    setSuccess('');
    setLaborSavingId(entry.id);
    try {
      const payload = profileToPayload(entry);
      if (!payload.name) {
        throw new Error('Name is required.');
      }
      const { data } = await api.patch(`/org/labor/${entry.id}`, payload);
      const updated = data?.labor;
      setLaborProfiles((prev) => prev.map((item) => (item.id === entry.id ? updated : item)));
      setSuccess('Labor profile updated.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to update labor profile.');
    } finally {
      setLaborSavingId(null);
    }
  };

  const deleteLaborProfile = async (laborId) => {
    setError('');
    setSuccess('');
    setLaborDeletingId(laborId);
    try {
      await api.delete(`/org/labor/${laborId}`);
      setLaborProfiles((prev) => prev.filter((entry) => entry.id !== laborId));
      setSuccess('Labor profile removed.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to remove labor profile.');
    } finally {
      setLaborDeletingId(null);
    }
  };

  const toggleMemberFeature = async (key, enabled) => {
    if (!selectedMemberId) return;
    setFeatureSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.patch(`/org/members/${selectedMemberId}/features`, {
        key,
        enabled,
      });

      setMembers((prev) =>
        prev.map((member) =>
          member.id === selectedMemberId
            ? { ...member, feature_overrides: data?.feature_overrides || {} }
            : member
        )
      );

      if (selectedMemberId === profile?.id) {
        await refresh();
      }

      setSuccess('Feature access updated.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unable to update feature access.');
    } finally {
      setFeatureSaving(false);
    }
  };

  const planLabel = PLAN_LABELS[organization?.plan] || 'Trial';
  const selectedMember = useMemo(
    () => members.find((member) => member.id === selectedMemberId) || null,
    [members, selectedMemberId]
  );
  const effectiveFeatureFlags = useMemo(() => {
    return FEATURE_KEYS.reduce((acc, key) => {
      let value = DEFAULT_FEATURE_FLAGS[key] ?? true;
      const orgFlag = getFlagValue(organization?.feature_flags, key);
      if (orgFlag === true) {
        value = true;
      } else if (orgFlag === false) {
        value = false;
      }
      const overrideValue = getFlagValue(selectedMember?.feature_overrides, key);
      if (typeof overrideValue === 'boolean') {
        value = overrideValue;
      }
      acc[key] = value;
      return acc;
    }, {});
  }, [organization?.feature_flags, selectedMember]);

  if (permissions.role !== 'admin' && !permissions.canAccessAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10 text-center text-zinc-200">
        <div className={`${card} p-8`}>
          <h1 className="text-2xl font-semibold mb-2">Admin access required</h1>
          <p className="text-sm text-zinc-400">
            Only workspace admins can access the control panel. Ask an admin to update your role.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 text-zinc-200 space-y-6">
      <div className={`${card} p-6`}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-zinc-400 uppercase tracking-wide">
              {organization?.name || 'Workspace'}
            </p>
            <h1 className="text-3xl font-semibold tracking-tight">
              <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
                Admin Console
              </span>
            </h1>
            <p className="text-sm text-zinc-400">
              Plan: {planLabel} · Status: {organization?.plan_status || 'active'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={refresh}
              className="rounded-xl border border-white/20 px-4 py-2 text-sm text-zinc-200 hover:bg-white/5"
            >
              Refresh
            </button>
          </div>
        </div>
        {(error || success) && (
          <div
            className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
              error
                ? 'border-red-500/40 bg-red-500/10 text-red-200'
                : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
            }`}
          >
            {error || success}
          </div>
        )}
      </div>
      <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className={`${card} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Members</h2>
              <p className="text-sm text-zinc-500">Manage teammates and their roles.</p>
            </div>
            <span className="text-sm text-zinc-400">{members.length} total</span>
          </div>
          {loadingMembers ? (
            <p className="text-sm text-zinc-400">Loading members...</p>
          ) : (
            <div className="space-y-4">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="glass-panel flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <p className="font-medium text-zinc-100">{member.full_name || member.email}</p>
                    <p className="text-xs text-zinc-500">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={member.role}
                      onChange={(event) => updateRole(member.id, event.target.value)}
                      className="rounded-md border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="text-sm text-red-400 hover:text-red-200"
                      onClick={() => removeMember(member.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {!members.length && <p className="text-sm text-zinc-500">No members yet.</p>}
            </div>
          )}
        </div>

        <div className={`${card} p-6 space-y-6`}>
          <div>
            <h3 className="text-lg font-semibold">Invite member</h3>
            <form className="mt-4 space-y-3" onSubmit={handleInvite}>
              <input
                type="email"
                className={input}
                placeholder="email@company.com"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
              />
              <select
                className={input}
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button type="submit" className={`${btnPrimary} w-full`} disabled={inviteBusy}>
                {inviteBusy ? 'Sending...' : 'Send invite'}
              </button>
            </form>
          </div>

          <div>
            <h4 className="text-md font-semibold mb-2">Pending invites</h4>
            <div className="space-y-2">
              {invites.length === 0 && <p className="text-sm text-zinc-500">No pending invites.</p>}
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="glass-panel flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-zinc-100">{invite.email}</p>
                    <p className="text-xs text-zinc-500">Role: {ensureRole(invite.role)}</p>
                  </div>
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:text-red-200"
                    onClick={() => revokeInvite(invite.id)}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {permissions.canAccessLabor ? (
        <section className={`${card} p-6 space-y-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Labor settings</h2>
              <p className="text-sm text-zinc-500">Track internal crews, hourly rates, and contacts.</p>
            </div>
            {laborLoading && <p className="text-xs text-zinc-400">Loading...</p>}
          </div>

          <form
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
            onSubmit={createLaborProfile}
          >
            <input
              className={input}
              placeholder="Name (required)"
              value={laborDraft.name}
              onChange={(event) => handleLaborDraftChange('name', event.target.value)}
            />
            <input
              className={input}
              placeholder="Job title"
              value={laborDraft.jobTitle}
              onChange={(event) => handleLaborDraftChange('jobTitle', event.target.value)}
            />
            <input
              className={input}
              type="number"
              min="0"
              step="1"
              placeholder="Crew number"
              value={laborDraft.crewNumber}
              onChange={(event) => handleLaborDraftChange('crewNumber', event.target.value)}
            />
            <input
              className={input}
              type="number"
              min="0"
              step="0.01"
              placeholder="Hourly rate"
              value={laborDraft.hourlyRate}
              onChange={(event) => handleLaborDraftChange('hourlyRate', event.target.value)}
            />
            <input
              className={input}
              type="email"
              placeholder="Contact email"
              value={laborDraft.contactEmail}
              onChange={(event) => handleLaborDraftChange('contactEmail', event.target.value)}
            />
            <textarea
              className={`${input} min-h-[90px]`}
              placeholder="Notes"
              value={laborDraft.notes}
              onChange={(event) => handleLaborDraftChange('notes', event.target.value)}
            />
            <button
              type="submit"
              className={`${btnPrimary} md:col-span-2 lg:col-span-3`}
              disabled={laborCreateBusy}
            >
              {laborCreateBusy ? 'Adding...' : 'Add labor role'}
            </button>
          </form>

          <div className="space-y-4">
            {laborProfiles.length === 0 && !laborLoading && (
              <p className="text-sm text-zinc-500">No labor roles added yet.</p>
            )}
            {laborProfiles.map((entry) => (
              <div
                key={entry.id}
                className="glass-card space-y-4 p-4"
              >
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <input
                    className={input}
                    value={entry.name || ''}
                    onChange={(event) => handleLaborFieldChange(entry.id, 'name', event.target.value)}
                    placeholder="Name"
                  />
                  <input
                    className={input}
                    value={entry.job_title || ''}
                    onChange={(event) =>
                      handleLaborFieldChange(entry.id, 'job_title', event.target.value)
                    }
                    placeholder="Job title"
                  />
                  <input
                    className={input}
                    type="number"
                    min="0"
                    step="1"
                    value={entry.crew_number ?? ''}
                    onChange={(event) =>
                      handleLaborFieldChange(entry.id, 'crew_number', event.target.value)
                    }
                    placeholder="Crew number"
                  />
                  <input
                    className={input}
                    type="number"
                    min="0"
                    step="0.01"
                    value={entry.hourly_rate ?? ''}
                    onChange={(event) =>
                      handleLaborFieldChange(entry.id, 'hourly_rate', event.target.value)
                    }
                    placeholder="Hourly rate"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <input
                    className={input}
                    type="email"
                    value={entry.contact_email || ''}
                    onChange={(event) =>
                      handleLaborFieldChange(entry.id, 'contact_email', event.target.value)
                    }
                    placeholder="Contact email"
                  />
                  <textarea
                    className={`${input} min-h-[80px]`}
                    value={entry.notes || ''}
                    onChange={(event) =>
                      handleLaborFieldChange(entry.id, 'notes', event.target.value)
                    }
                    placeholder="Notes"
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    className={btnPrimary}
                    onClick={() => saveLaborProfile(entry)}
                    disabled={laborSavingId === entry.id}
                  >
                    {laborSavingId === entry.id ? 'Saving...' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-red-400/40 px-4 py-2 text-sm text-red-300 transition hover:bg-red-500/10 disabled:opacity-60"
                    onClick={() => deleteLaborProfile(entry.id)}
                    disabled={laborDeletingId === entry.id}
                  >
                    {laborDeletingId === entry.id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className={`${card} p-6`}>
          <div className="space-y-2 text-sm text-zinc-400">
            <h2 className="text-xl font-semibold text-zinc-100">Labor settings</h2>
            <p>This module is disabled for this workspace. Enable the Labor feature flag to manage crew roles.</p>
          </div>
        </section>
      )}

      <section className={`${card} p-6 space-y-5`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Feature access</h2>
            <p className="text-sm text-zinc-500">
              Modules start enabled for everyone. Use overrides to turn features off for specific teammates.
            </p>
          </div>
          {featureSaving && <p className="text-xs text-zinc-400">Saving...</p>}
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-zinc-500">Invite teammates to manage feature access.</p>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <label className="text-sm text-zinc-400">
                Managing access for
              </label>
              <select
                className="glass-input w-full sm:w-72 text-sm"
                value={selectedMemberId || ''}
                onChange={(event) => setSelectedMemberId(event.target.value || null)}
              >
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.email} {member.id === profile?.id ? '(You)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedMember ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURE_CONTROLS.map(({ key, label, blurb }) => {
                  const enabled = effectiveFeatureFlags[key] !== false;
                  return (
                    <label
                      key={key}
                      className="glass-panel flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm"
                    >
                      <div>
                        <p className="font-medium text-zinc-100">{label}</p>
                        <p className="text-xs text-zinc-500">
                          {enabled ? 'Access enabled' : 'Access disabled'}
                        </p>
                        {blurb && <p className="text-[11px] text-zinc-500/80 mt-1">{blurb}</p>}
                      </div>
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={featureSaving}
                        onChange={(event) => toggleMemberFeature(key, event.target.checked)}
                      />
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">Select a teammate to edit feature access.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
